import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useUserStore } from "../../store/userStore";
import { useActiveAppStore } from "../../store/activeAppStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { ADMIN_RESELLER_ROLE_ID } from "../../hooks/useIsReseller";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import Swal from "sweetalert2";
import { faRobot } from "@fortawesome/free-solid-svg-icons";

export default function AiLayout() {
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);

  const companyId = useCompaniesStore((state) => state.companyId);
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const fetchAllCompanies = useCompaniesStore((state) => state.fetchAllCompanies);
  const setCompanyId = useCompaniesStore((state) => state.setCompanyId);

  // Verificar si es reseller por ROL (no por compañía)
  const isReseller = (localStorage.getItem("userRoleId") || "") === ADMIN_RESELLER_ROLE_ID;

  const setActiveApp = useActiveAppStore((state) => state.setActiveApp);
  useEffect(() => {
    setActiveApp(
      "ai",
      t("apps.ai.name"),
      t("apps.ai.description"),
      faRobot,
      "bg-emerald-500"
    );
  }, [setActiveApp, t]);

  // Sincronizar companyId desde localStorage al montar
  useEffect(() => {
    const storedCompanyId = localStorage.getItem("companyId");
    const storedCompanyUser = localStorage.getItem("userCompanyId");
    if (storedCompanyId && storedCompanyId !== companyId) {
      setCompanyId(storedCompanyId);
    } else if (!storedCompanyId && storedCompanyUser && !isReseller) {
      setCompanyId(storedCompanyUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReseller && allCompanies.length === 0) {
      fetchAllCompanies();
    }
  }, [isReseller, allCompanies.length, fetchAllCompanies]);

  const {
    isExpiringSoon,
    timeRemainingFormatted,
    isExpired,
    isAuthenticated,
    isInitialized,
  } = useSessionCheck({
    warningThreshold: 60 * 60 * 1000,
    autoRefresh: true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.id === "ai") {
      closeAppsSidebar();
      return;
    }

    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }

    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank", "noopener,noreferrer");
    }
  };

  useEffect(() => {
    if (
      isInitialized &&
      (isExpired || !isAuthenticated) &&
      !hasRedirected.current
    ) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user) {
      if (location.pathname !== "/splash") {
        localStorage.setItem("lastRoute", location.pathname);
        navigate("/splash");
      }
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className={`h-screen flex flex-col ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>
            {t("sessionBanner.expiring", { time: timeRemainingFormatted })}
          </span>
        </div>
      )}

      <Header
        onOpenAppsSidebar={openAppsSidebar}
        isDarkMode={isDarkMode}
        isReseller={isReseller}
        companies={allCompanies}
        selectedCompany={companyId}
        onCompanyChange={setCompanyId}
      />

      <div className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="ai"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        isDarkMode={isDarkMode}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          username: user.username,
        } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
